// React Query hooks for the Skip Management report endpoints
// (/api/v1/skip/reports/*). Each report is date-range filtered and only
// fires when both bounds are set (the page gates `enabled` on that).

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';

export interface DateRange {
  date_from: string;
  date_to: string;
}

// --- response shapes (mirror server/src/modules/skip/reports/types.ts) ---

export interface JobsOverviewResponse {
  kpis: {
    total_jobs: number;
    total_revenue: number;
    completed_jobs: number;
    cancellation_rate: number;
  };
  by_status: Array<{ status: string; jobs: number; revenue: number }>;
  by_type: Array<{ type_name: string; jobs: number; revenue: number }>;
}

export interface EnquiryFunnelResponse {
  kpis: {
    total_enquiries: number;
    converted: number;
    conversion_rate: number;
    avg_days_to_convert: number;
  };
  by_status: Array<{
    status: string;
    enquiries: number;
    est_value: number;
    avg_contact_attempts: number;
  }>;
  contact_outcomes: Array<{ outcome: string; attempts: number }>;
}

export interface InventoryUtilisationResponse {
  kpis: {
    total_units: number;
    out_units: number;
    available_units: number;
    utilisation_rate: number;
  };
  by_status: Array<{ status: string; units: number }>;
  by_type: Array<{ type_name: string; units: number }>;
  units: Array<{
    skip_number: string;
    stock_type_name: string;
    size_name: string;
    status: string;
    skip_condition: string;
    jobs_in_period: number;
  }>;
}

export interface CustomerRevenueResponse {
  kpis: {
    active_customers: number;
    total_revenue: number;
    avg_job_value: number;
    outstanding: number;
  };
  top_by_revenue: Array<{ customer_name: string; jobs: number; revenue: number }>;
  top_by_volume: Array<{ customer_name: string; jobs: number; revenue: number }>;
  customers: Array<{
    customer_name: string;
    jobs: number;
    revenue: number;
    outstanding: number;
    last_job_date: string | null;
  }>;
}

function useSkipReport<T>(key: string, path: string, range: DateRange, enabled: boolean) {
  const params = new URLSearchParams({
    date_from: range.date_from,
    date_to: range.date_to,
  }).toString();
  return useQuery<T>({
    queryKey: ['skip-reports', key, range] as const,
    queryFn: async () => {
      const res = await apiClient.get<T>(`/skip/reports/${path}?${params}`);
      return res.data;
    },
    enabled,
    retry: false,
  });
}

export function useJobsOverview(range: DateRange, enabled: boolean) {
  return useSkipReport<JobsOverviewResponse>('jobs-overview', 'jobs-overview', range, enabled);
}

export function useEnquiryFunnel(range: DateRange, enabled: boolean) {
  return useSkipReport<EnquiryFunnelResponse>('enquiry-funnel', 'enquiry-funnel', range, enabled);
}

export function useInventoryUtilisation(range: DateRange, enabled: boolean) {
  return useSkipReport<InventoryUtilisationResponse>(
    'inventory-utilisation',
    'inventory-utilisation',
    range,
    enabled,
  );
}

export function useCustomerRevenue(range: DateRange, enabled: boolean) {
  return useSkipReport<CustomerRevenueResponse>(
    'customer-revenue',
    'customer-revenue',
    range,
    enabled,
  );
}
